import { useFeatureFlagEnabled, FF, useSplitFlag } from 'modules/feature-flags';
import { FilterVariant, WorklistFilterSourceName, WorklistFilters } from '../types';
import { useWorklistCustomStatuses, formatStatusName } from 'hooks/useWorklistCustomStatuses';
import { useMemo } from 'react';
import type {
  DateRangeFilterType,
  FilterDefinitionType,
  MultiSelectFilterItemType,
  MultiSelectFilterType,
  NumberRangeFilterType,
  TextFilterType,
} from '../types';
import { WorklistSurfaceType, WorklistSurface } from '../../Worklist/types';
import { subDays } from 'date-fns';
import type { FilterConfig } from '../constants';
import {
  GetModalitiesQuery,
  GetModalitiesQueryVariables,
  WorklistItemStatus,
} from 'generated/graphql';
import { GET_MODALITIES } from 'modules/Apollo/queries';
import { useQuery } from '@apollo/client';

type useGenerateFilterConfigReturn = {
  surfaceToFilterConfig: FilterConfig;
};
export const useGenerateFilterConfig = (
  defaultSurface?: WorklistSurfaceType
): useGenerateFilterConfigReturn => {
  const [isWorklistImprovedFiltersEnabled, isWorklistImprovedFiltersLoading] =
    useFeatureFlagEnabled(FF.WORKLIST_IMPROVED_FILTERS);
  const [isWorklistDerivedNameFiltersEnabled] = useFeatureFlagEnabled(
    FF.WORKLIST_DERIVED_NAME_FILTERS
  );
  const [isQcStatusColumnEnabled] = useFeatureFlagEnabled(FF.QC_STATUS_COLUMN);

  const recentlyReadWindow = useSplitFlag(FF.WORKLIST_RECENTLY_READ_DAYS, (flag) =>
    Number(flag)
  )[0];

  // Keeping this for now so that we can still use the dropdown
  // in the legacy version
  const { data: modalityData, loading: isModalityLoading } = useQuery<
    GetModalitiesQuery,
    GetModalitiesQueryVariables
  >(GET_MODALITIES, {
    fetchPolicy: 'cache-first',
    skip: defaultSurface !== WorklistSurface.STUDIES_DIALOG,
  });

  const modalityItems = useMemo(() => {
    return (modalityData?.modalities ?? []).map((modality) => ({
      value: modality.name,
      label: modality.name,
    }));
  }, [modalityData]);

  const { customStatusFilters, isLoadingCustomStatuses } = useWorklistCustomStatuses();

  const defaultFilters = useMemo(() => {
    const baseFilters: FilterDefinitionType = {
      [WorklistFilters.PatientSex]: {
        label: 'Gender',
        type: FilterVariant.MultiSelect,
        isSearchable: false,
        source: WorklistFilterSourceName.PatientSex,
        items: isWorklistImprovedFiltersEnabled
          ? undefined
          : [
              { value: 'F', label: 'Female' },
              { value: 'M', label: 'Male' },
            ],
      } as MultiSelectFilterType,
      [WorklistFilters.StudyDate]: {
        label: 'Exam Date',
        type: FilterVariant.DateRange,
        isLoading: isWorklistImprovedFiltersLoading,
      } as DateRangeFilterType,
      [WorklistFilters.PatientDob]: {
        label: 'DOB',
        type: FilterVariant.DateRange,
        isLoading: isWorklistImprovedFiltersLoading,
      } as DateRangeFilterType,
      [WorklistFilters.Views]: {
        label: 'Views',
        type: FilterVariant.MultiSelect,
        items: [],
        hidden: true,
        source: '' as const,
      } as MultiSelectFilterType,
      [WorklistFilters.Modality]: {
        label: 'Modality',
        type: FilterVariant.MultiSelect,
        isSearchable: isWorklistImprovedFiltersEnabled,
        source: WorklistFilterSourceName.Modality,
        items: isWorklistImprovedFiltersEnabled ? undefined : modalityItems,
      } as MultiSelectFilterType,
      [WorklistFilters.SubmittedAt]: {
        type: FilterVariant.DateRange,
        label: 'Submitted At',
      } as DateRangeFilterType,
    };

    return baseFilters;
  }, [isWorklistImprovedFiltersEnabled, isWorklistImprovedFiltersLoading, modalityItems]);

  const worklistSurfaceFilters = useMemo(() => {
    const baseWorklistSurfaceFilters: FilterDefinitionType = {
      [WorklistFilters.HasDraftReport]: {
        label: 'Draft',
        type: FilterVariant.MultiSelect,
        items: [
          { value: 'true', label: 'Has Draft' },
          { value: 'false', label: 'No Draft' },
        ],
        isBoolean: true,
        source: WorklistFilterSourceName.HasDraftReport,
        isLoading: isWorklistImprovedFiltersLoading,
        formatItems: (items: MultiSelectFilterItemType[]) =>
          items.map((item) => ({
            value: String(item.value).toLowerCase(),
            label: item.value === 'True' ? 'Has Draft' : 'No Draft',
            count: item.count,
          })),
      } as MultiSelectFilterType,
    };

    baseWorklistSurfaceFilters[WorklistFilters.CustomStatus] = {
      label: 'Workflow Status',
      type: FilterVariant.MultiSelect,
      isLoading: isWorklistImprovedFiltersLoading || isLoadingCustomStatuses,
      source: WorklistFilterSourceName.CustomStatus,
      items: isWorklistImprovedFiltersEnabled ? undefined : customStatusFilters,
      formatItems: (items: MultiSelectFilterItemType[]) =>
        items.map((item) => ({
          label: formatStatusName(item.label),
          value: item.value,
          count: item.count,
        })),
    } as MultiSelectFilterType;

    if (isWorklistImprovedFiltersEnabled) {
      const excludedSites = /(?:-)?(?:MISMATCHED[ _]STUDY|PRIOR[ _]STUDY)$/i;

      baseWorklistSurfaceFilters[WorklistFilters.Age] = {
        label: 'Age',
        type: FilterVariant.NumberRange,
        isLoading: isWorklistImprovedFiltersLoading,
      } as NumberRangeFilterType;
      baseWorklistSurfaceFilters[WorklistFilters.AccessionNumber] = {
        label: 'Accession #',
        type: FilterVariant.Text,
      } as TextFilterType;
      baseWorklistSurfaceFilters[WorklistFilters.Mrn] = {
        label: 'MRN #',
        type: FilterVariant.Text,
      } as TextFilterType;
      baseWorklistSurfaceFilters[WorklistFilters.PatientType] = {
        label: 'Patient Type',
        type: FilterVariant.MultiSelect,
        isSearchable: false,
        source: WorklistFilterSourceName.PatientType,
      } as MultiSelectFilterType;
      baseWorklistSurfaceFilters[WorklistFilters.StudyDescription] = {
        label: 'Exam Description',
        type: FilterVariant.Text,
      } as TextFilterType;
      baseWorklistSurfaceFilters[WorklistFilters.Site] = {
        label: 'Site',
        type: FilterVariant.MultiSelect,
        isSearchable: true,
        source: WorklistFilterSourceName.Site,
        formatItems: (items: MultiSelectFilterItemType[]) =>
          items.filter((item) => {
            return !excludedSites.test(item.label);
          }),
      } as MultiSelectFilterType;
      baseWorklistSurfaceFilters[WorklistFilters.BodyPart] = {
        label: 'Body Part',
        type: FilterVariant.MultiSelect,
        isSearchable: true,
        source: WorklistFilterSourceName.BodyPart,
      } as MultiSelectFilterType;
      baseWorklistSurfaceFilters[WorklistFilters.PatientName] = {
        label: 'Name',
        type: FilterVariant.Text,
        placeholder: 'Search (last name only)',
      } as TextFilterType;
      baseWorklistSurfaceFilters[WorklistFilters.Priority] = {
        label: 'Priority',
        type: FilterVariant.MultiSelect,
        isSearchable: false,
        source: WorklistFilterSourceName.Priority,
      };
      baseWorklistSurfaceFilters[WorklistFilters.SignedBy] = {
        label: 'Signed By',
        type: FilterVariant.Text,
        placeholder: 'Search (last name only)',
      } as TextFilterType;

      if (isWorklistDerivedNameFiltersEnabled) {
        baseWorklistSurfaceFilters[WorklistFilters.Radiologist] = {
          label: 'Radiologist',
          type: FilterVariant.Text,
          placeholder: 'Search (last name only)',
        } as TextFilterType;
        baseWorklistSurfaceFilters[WorklistFilters.ReferringPhysician] = {
          label: 'Ordering Physician',
          type: FilterVariant.Text,
          placeholder: 'Search (last name only)',
        } as TextFilterType;
      }
    }

    return baseWorklistSurfaceFilters;
  }, [
    customStatusFilters,
    isLoadingCustomStatuses,
    isWorklistDerivedNameFiltersEnabled,
    isWorklistImprovedFiltersEnabled,
    isWorklistImprovedFiltersLoading,
  ]);

  const surfaceToFilterConfig = useMemo(
    () => ({
      [WorklistSurface.MY_QUEUE]: {
        lockedFilters: {},
        defaultFilters: { ...defaultFilters, ...worklistSurfaceFilters },
        hiddenFilters: [WorklistFilters.SubmittedAt, WorklistFilters.PatientSearch],
      },
      [WorklistSurface.PENDING]: {
        lockedFilters: {},
        defaultFilters: { ...defaultFilters, ...worklistSurfaceFilters },
        hiddenFilters: [WorklistFilters.SubmittedAt, WorklistFilters.PatientSearch],
      },
      [WorklistSurface.IN_PROGRESS]: {
        lockedFilters: {},
        defaultFilters: {
          ...defaultFilters,
          ...worklistSurfaceFilters,
          [WorklistFilters.Status]: {
            label: 'Case Status',
            type: FilterVariant.MultiSelect,
            items: [
              { value: 'Locked', label: 'Locked' },
              { value: 'Claimed', label: 'Claimed' },
            ],
            // This filter be shown once the BE performance is improved
            hidden: true,
            source: WorklistFilterSourceName.Status,
          },
        },
        hiddenFilters: [WorklistFilters.SubmittedAt, WorklistFilters.PatientSearch],
      },
      [WorklistSurface.RECENTLY_READ]: {
        lockedFilters: {
          [WorklistFilters.SubmittedAt]: [
            subDays(new Date(), recentlyReadWindow).toLocaleDateString(),
            null,
          ],
        },
        defaultFilters: {
          ...defaultFilters,
          ...worklistSurfaceFilters,
        },
        hiddenFilters: [WorklistFilters.HasDraftReport, WorklistFilters.PatientSearch],
      },
      [WorklistSurface.QC]: {
        lockedFilters: {},
        defaultFilters: {
          ...defaultFilters,
          ...worklistSurfaceFilters,
          ...(isQcStatusColumnEnabled
            ? {
                [WorklistFilters.QcStatus]: {
                  label: 'Status',
                  type: FilterVariant.MultiSelect,
                  isSearchable: false,
                  items: [
                    { value: WorklistItemStatus.Qc, label: 'QC', defaultSelected: true },
                    {
                      value: WorklistItemStatus.InTransit,
                      label: 'In Transit',
                      defaultSelected: true,
                    },
                    {
                      value: WorklistItemStatus.Mismatched,
                      label: 'Mismatched',
                      defaultSelected: true,
                    },
                    {
                      value: WorklistItemStatus.Scheduled,
                      label: 'Scheduled',
                      defaultSelected: true,
                    },
                  ],
                },
              }
            : undefined),
        },
      },
      [WorklistSurface.ARCHIVE]: {
        lockedFilters: {},
        defaultFilters: {
          ...defaultFilters,
          ...worklistSurfaceFilters,
          [WorklistFilters.SubmittedAt]: {
            type: FilterVariant.DateRange,
            label: 'Submitted At',
          } as DateRangeFilterType,
        },
        hiddenFilters: [WorklistFilters.HasDraftReport, WorklistFilters.PatientSearch],
      },
      [WorklistSurface.STUDIES_DIALOG]: {
        lockedFilters: {
          [WorklistFilters.PatientSearch]: ['true'],
        },
        defaultFilters: {
          [WorklistFilters.PatientSex]: {
            label: 'Gender',
            type: FilterVariant.MultiSelect,
            items: [
              { value: 'F', label: 'Female' },
              { value: 'M', label: 'Male' },
            ],
          } as MultiSelectFilterType,
          [WorklistFilters.StudyDate]: {
            label: 'Study Date',
            type: FilterVariant.DateRange,
          } as DateRangeFilterType,
          [WorklistFilters.PatientDob]: {
            label: 'DOB',
            type: FilterVariant.DateRange,
          } as DateRangeFilterType,
          [WorklistFilters.Modality]: {
            label: 'Modality',
            type: FilterVariant.MultiSelect,
            items: modalityItems,
            isLoading: isModalityLoading,
          } as MultiSelectFilterType,
          [WorklistFilters.PatientSearch]: {
            label: 'Patient Search',
            type: FilterVariant.Toggle,
            defaultValue: true,
            hidden: true,
          },
        },
        hiddenFilters: [WorklistFilters.HasDraftReport, WorklistFilters.CustomStatus],
      },
      [WorklistSurface.WORKLIST_VIEW]: {
        lockedFilters: {},
        defaultFilters: {
          ...defaultFilters,
          ...worklistSurfaceFilters,
        },
        hiddenFilters: [],
      },
      [WorklistSurface.PATIENT_JACKET_PRIORS]: {
        lockedFilters: {
          [WorklistFilters.PatientSearch]: ['true'],
        },
        defaultFilters: {
          ...defaultFilters,
          [WorklistFilters.PatientSearch]: {
            label: 'Patient Search',
            type: FilterVariant.Toggle,
            defaultValue: true,
          },
          [WorklistFilters.StudyDate]: {
            label: 'Study Date',
            type: FilterVariant.DateRange,
            isLoading: isWorklistImprovedFiltersLoading,
          } as DateRangeFilterType,
          [WorklistFilters.StudyDescription]: {
            label: 'Exam Description',
            type: FilterVariant.Text,
          } as TextFilterType,
          [WorklistFilters.BodyPart]: {
            label: 'Body Part',
            type: FilterVariant.MultiSelect,
            isSearchable: true,
            source: WorklistFilterSourceName.BodyPart,
          } as MultiSelectFilterType,
        },
        hiddenFilters: [],
      },
      [WorklistSurface.GLOBAL_SEARCH]: {
        lockedFilters: {},
        defaultFilters: {
          ...defaultFilters,
          ...worklistSurfaceFilters,
        },
        hiddenFilters: [],
      },
      [WorklistSurface.CUSTOM_WORKLIST]: {
        lockedFilters: {},
        defaultFilters: {
          ...defaultFilters,
          ...worklistSurfaceFilters,
          [WorklistFilters.Status]: {
            label: 'Status',
            source: WorklistFilterSourceName.Status,
            type: FilterVariant.MultiSelect,
            hidden: true,
          } as FilterDefinitionType,
        },
        hiddenFilters: [],
      },
    }),
    [
      defaultFilters,
      worklistSurfaceFilters,
      recentlyReadWindow,
      isQcStatusColumnEnabled,
      modalityItems,
      isModalityLoading,
      isWorklistImprovedFiltersLoading,
    ]
  );

  // @ts-expect-error [EN-10263] - TS2322 - Type '{ surfaceToFilterConfig: { MY_QUEUE: { lockedFilters: {}; defaultFilters: { accessionNumber?: FilterType | undefined; age?: FilterType | undefined; bodyPart?: FilterType | undefined; ... 17 more ...; patientSearch?: FilterType | undefined; }; hiddenFilters: (WorklistColumnId.SubmittedAt | "patientSearch")[]; }; ... ...' is not assignable to type 'useGenerateFilterConfigReturn'.
  return useMemo(
    () => ({
      surfaceToFilterConfig,
    }),
    [surfaceToFilterConfig]
  );
};
